import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';

const SESSION_KEY = 'eddy_session_id';

function getOrCreateSessionId(): string {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export interface SessionState {
  sessionId: string;
}

export type SessionStore = SessionState;

function createSessionStore(): StoreApi<SessionStore> {
  return createStore<SessionStore>('session-store', () => ({
    sessionId: getOrCreateSessionId(),
  }));
}

let store: StoreApi<SessionStore> | undefined;

export function getSessionStore(): StoreApi<SessionStore> {
  if (!store) store = createSessionStore();
  return store;
}

if (import.meta.hot) {
  import.meta.hot.accept(() => { store = undefined; });
}

/**
 * Override the session id with the server-resolved canonical one (the user's
 * current conversation), and cache it so the next boot's first paint isn't a
 * stale local id. The backend value is the source of truth; the localStorage
 * value is only a cache. Called once on boot from the app-layout auth gate.
 */
export function setSessionId(id: string): void {
  if (!id) return;
  localStorage.setItem(SESSION_KEY, id);
  getSessionStore().setState({ sessionId: id });
}

export function useSessionStore<T>(selector: (state: SessionStore) => T): T {
  return useStore(getSessionStore(), selector);
}

export function useSession() {
  const sessionId = useSessionStore((s) => s.sessionId);
  return { sessionId };
}
